import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function authHeaders(token) {
  return { headers: { 'x-auth-token': token } };
}

export async function createCall({ token, clientId, conversationId }) {
  const res = await axios.post(
    `${API_BASE_URL}/calls`,
    { clientId, conversationId },
    authHeaders(token)
  );
  return res.data;
}

export async function joinCall({ token, liveCallId }) {
  const res = await axios.post(
    `${API_BASE_URL}/calls/${liveCallId}/join`,
    {},
    authHeaders(token)
  );
  return res.data;
}

export async function endCall({ token, liveCallId }) {
  const res = await axios.post(
    `${API_BASE_URL}/calls/${liveCallId}/end`,
    {},
    authHeaders(token)
  );
  return res.data;
}

export async function declineCall({ token, liveCallId }) {
  const res = await axios.post(
    `${API_BASE_URL}/calls/${liveCallId}/decline`,
    {},
    authHeaders(token)
  );
  return res.data;
}

export async function getCall({ token, liveCallId }) {
  const res = await axios.get(
    `${API_BASE_URL}/calls/${liveCallId}`,
    authHeaders(token)
  );
  return res.data;
}

export async function getCallTranscript({ token, liveCallId }) {
  const res = await axios.get(
    `${API_BASE_URL}/calls/${liveCallId}/transcript`,
    authHeaders(token)
  );
  return res.data;
}

export async function listRecentCalls({ token, limit = 50 }) {
  const res = await axios.get(
    `${API_BASE_URL}/calls?recent=1&limit=${limit}`,
    authHeaders(token)
  );
  return res.data;
}

/**
 * Therapist-only: list all calls for a specific client (no time cutoff).
 */
export async function listCallsForClient({ token, clientId, limit = 100 }) {
  const res = await axios.get(
    `${API_BASE_URL}/calls?clientId=${encodeURIComponent(clientId)}&limit=${limit}`,
    authHeaders(token)
  );
  return res.data;
}

/**
 * Client-only: recent call history for the missed-call banner (last 24h default).
 */
export async function listMyCallHistory({ token, hours = 24 }) {
  const res = await axios.get(
    `${API_BASE_URL}/calls/history-for-me?hours=${hours}`,
    authHeaders(token)
  );
  return res.data;
}

/**
 * Therapist-only: save in-call notes (debounced from caller).
 */
export async function updateCallNotes({ token, liveCallId, notes }) {
  const res = await axios.patch(
    `${API_BASE_URL}/calls/${liveCallId}/notes`,
    { notes },
    authHeaders(token)
  );
  return res.data;
}

/**
 * Therapist-only: trigger an LLM cleanup pass over the raw transcript.
 * Returns the updated transcript doc.
 */
export async function runTranscriptCleanup({ token, liveCallId }) {
  const res = await axios.post(
    `${API_BASE_URL}/calls/${liveCallId}/clean-transcript`,
    {},
    { ...authHeaders(token), timeout: 180000 }
  );
  return res.data;
}

/**
 * Therapist-only: save an edited transcript. Raw + cleaned are never mutated.
 * `source` is the version the therapist started from ('raw' | 'cleaned' | 'edited').
 */
export async function saveEditedTranscript({ token, liveCallId, messages, source }) {
  const res = await axios.put(
    `${API_BASE_URL}/calls/${liveCallId}/transcript/edited`,
    { messages, source },
    authHeaders(token)
  );
  return res.data;
}

/** Client records consent to transcription before joining. */
export async function recordCallConsent({ token, liveCallId, copyVersion }) {
  const res = await axios.post(
    `${API_BASE_URL}/calls/${liveCallId}/consent`,
    { copyVersion },
    authHeaders(token)
  );
  return res.data;
}

export async function getCallSummary({ token, liveCallId }) {
  const res = await axios.get(
    `${API_BASE_URL}/calls/${liveCallId}/summary`,
    authHeaders(token)
  );
  return res.data;
}

export async function generateCallSummaryApi({ token, liveCallId }) {
  const res = await axios.post(
    `${API_BASE_URL}/calls/${liveCallId}/summary`,
    {},
    { ...authHeaders(token), timeout: 60000 }
  );
  return res.data;
}

export async function saveFinalSummary({ token, liveCallId, text }) {
  const res = await axios.put(
    `${API_BASE_URL}/calls/${liveCallId}/summary/final`,
    { text },
    authHeaders(token)
  );
  return res.data;
}

export async function mergeTranscript({ token, liveCallId }) {
  const res = await axios.post(
    `${API_BASE_URL}/calls/${liveCallId}/merge-transcript`,
    {},
    authHeaders(token)
  );
  return res.data;
}

// ----------------------------------------------------------------------------
// Therapist-Led Mode (Spec E) — Dual summary lifecycle
// ----------------------------------------------------------------------------

export async function generateDualSummary({ token, liveCallId, force = false }) {
  const res = await axios.post(
    `${API_BASE_URL}/calls/${liveCallId}/dual-summary/generate`,
    { force },
    { ...authHeaders(token), timeout: 90000 }
  );
  return res.data;
}

export async function signTherapistNote({ token, liveCallId }) {
  const res = await axios.post(
    `${API_BASE_URL}/calls/${liveCallId}/dual-summary/sign`,
    {},
    authHeaders(token)
  );
  return res.data;
}

export async function shareClientSummary({ token, liveCallId }) {
  const res = await axios.post(
    `${API_BASE_URL}/calls/${liveCallId}/dual-summary/share`,
    {},
    authHeaders(token)
  );
  return res.data;
}

export async function withholdClientSummary({ token, liveCallId, reason }) {
  const res = await axios.post(
    `${API_BASE_URL}/calls/${liveCallId}/dual-summary/withhold`,
    { reason },
    authHeaders(token)
  );
  return res.data;
}

// AI Supporter access to video sessions (29 Sep 2026)
export async function setCallAiContext({ token, liveCallId, withheld, reason }) {
  const res = await axios.post(
    `${API_BASE_URL}/calls/${liveCallId}/ai-context`,
    { withheld, reason },
    authHeaders(token)
  );
  return res.data;
}

export async function getMyAiSharing({ token }) {
  const res = await axios.get(`${API_BASE_URL}/calls/ai-sharing/me`, authHeaders(token));
  return res.data;
}

export async function setMyAiSharing({ token, callContextForAI }) {
  const res = await axios.put(`${API_BASE_URL}/calls/ai-sharing/me`, { callContextForAI }, authHeaders(token));
  return res.data;
}

// ----------------------------------------------------------------------------
// Therapist-Led Mode (Spec F) — QA self-review (call-owner full detail)
// ----------------------------------------------------------------------------

export async function getQaSelfReview({ token, liveCallId, rubricKey }) {
  const params = rubricKey ? `?rubricKey=${encodeURIComponent(rubricKey)}` : '';
  const res = await axios.get(
    `${API_BASE_URL}/calls/${liveCallId}/qa/self-review${params}`,
    authHeaders(token)
  );
  return res.data;
}

/**
 * Returns any pending/ringing call targeting the caller (as client)
 * within the last 5 minutes. Used to recover from missed push events
 * after a socket reconnect.
 */
export async function getPendingCallForMe({ token }) {
  const res = await axios.get(
    `${API_BASE_URL}/calls/pending-for-me`,
    authHeaders(token)
  );
  return res.data;
}
